/**
 * Scenarios – LicensingAnalytics
 *
 * Renders licensing-specific results for a scenario:
 *   - Per-driver analytics cards: FY impact, Rolling 12 impact, breakeven
 *     usage (as a quantity, not currency), and a breakeven status/reason banner.
 *   - Current-model vs scenario-model monthly component economics table.
 *
 * Reads Scenario.licensingAnalytics and Scenario.licensingMonthlyComponents.
 *
 * breakevenUsage is a quantity (units consumed, not dollars).  It is formatted
 * as a compact/locale number, optionally suffixed with the record's usageUnit
 * when available.
 */

import {
  Activity,
  CheckCircle2,
  Info,
  TrendingUp,
} from "lucide-react";
import type {
  Scenario,
  ScenarioLicenseAnalytics,
  ScenarioLicenseMonthlyComponent,
} from "@workspace/api-client-react";
import { formatMoney, formatVariance } from "@/components/tekledger-ui";
import { periodLabel } from "./utils";
import {
  summarizeTiers,
  summarizeTrueUp,
} from "@/components/license-economics/types";

// ─── Quantity formatter (not currency) ────────────────────────────────────────

function formatQuantity(value: number, usageUnit?: string | null): string {
  const formatted = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
  return usageUnit ? `${formatted} ${usageUnit}` : formatted;
}

// ─── Breakeven banner ─────────────────────────────────────────────────────────

function BreakevenBanner({
  analytics,
  usageUnit,
}: {
  analytics: ScenarioLicenseAnalytics;
  usageUnit?: string | null;
}) {
  const status = analytics.breakevenStatus;
  const Icon =
    status === "available"
      ? CheckCircle2
      : status === "already_overtaken"
      ? TrendingUp
      : Info;

  return (
    <div
      className={`sc-le-breakeven sc-le-breakeven--${status}`}
      data-testid={`breakeven-${analytics.driverId}`}
    >
      <Icon size={14} />
      <div>
        <strong style={{ display: "block", marginBottom: 2 }}>
          {status === "available"
            ? "Breakeven available"
            : status === "already_overtaken"
            ? "Breakeven already overtaken"
            : "Breakeven not meaningful"}
          {analytics.breakevenUsage != null && (
            <>
              {" · "}
              {formatQuantity(analytics.breakevenUsage, usageUnit)}
            </>
          )}
        </strong>
        {analytics.breakevenReason}
      </div>
    </div>
  );
}

// ─── Per-driver analytics card ────────────────────────────────────────────────

function AnalyticsCard({
  analytics,
  usageUnit,
}: {
  analytics: ScenarioLicenseAnalytics;
  usageUnit?: string | null;
}) {
  return (
    <div
      className="sc-le-analytics-card"
      data-testid={`licensing-analytics-${analytics.driverId}`}
    >
      <div className="sc-le-analytics-head">
        <strong>
          License input{" "}
          <code className="le-code">
            {analytics.licenseEconomicsId.slice(0, 8)}…
          </code>
        </strong>
        <span className="sc-driver-type-tag">Licensing</span>
      </div>
      <div className="sc-le-analytics-grid">
        <div>
          <span>Utilization</span>
          <b>{analytics.activeSeats} / {analytics.contractedSeats} ({analytics.utilizationPercentage}%)</b>
        </div>
        <div>
          <span>Reclaim assumption</span>
          <b>{analytics.reclaimedSeats} of {analytics.unusedSeats} unused</b>
        </div>
        <div>
          <span>Available savings / mo.</span>
          <b className={analytics.monthlySavings > 0 ? "good-text" : ""}>{formatMoney(analytics.monthlySavings)}</b>
        </div>
        <div>
          <span>FY Impact</span>
          <b
            className={
              analytics.fyImpact > 0
                ? "bad-text"
                : analytics.fyImpact < 0
                ? "good-text"
                : ""
            }
          >
            {analytics.fyImpact === 0
              ? "—"
              : formatVariance(analytics.fyImpact)}
          </b>
        </div>
        <div>
          <span>Rolling 12M</span>
          <b
            className={
              analytics.rolling12Impact > 0
                ? "bad-text"
                : analytics.rolling12Impact < 0
                ? "good-text"
                : ""
            }
          >
            {analytics.rolling12Impact === 0
              ? "—"
              : formatVariance(analytics.rolling12Impact)}
          </b>
        </div>
        <div>
          <span>
            Breakeven usage
            {usageUnit ? ` (${usageUnit})` : ""}
          </span>
          <b>
            {analytics.breakevenUsage != null
              ? formatQuantity(analytics.breakevenUsage, usageUnit)
              : "—"}
          </b>
        </div>
      </div>
      <div className="sc-le-breakeven sc-le-breakeven--not_meaningful">
        <Info size={14} />
        <div>
          <strong>Effective {periodLabel(analytics.effectivePeriod)} FY{analytics.effectiveFiscalYear} · GL {analytics.glAccount}</strong>
          <div>{analytics.timingBasis === "renewal" ? "Contract renewal" : "Explicit forecast timing"} · Source {analytics.sourceReference}</div>
        </div>
      </div>
      <BreakevenBanner analytics={analytics} usageUnit={usageUnit} />
    </div>
  );
}

// ─── Monthly components table ─────────────────────────────────────────────────

function ComponentsTable({
  components,
}: {
  components: ScenarioLicenseMonthlyComponent[];
}) {
  return (
    <div className="table-wrap">
      <table className="tl-fy-table sc-le-components-table">
        <thead>
          <tr>
            <th>Period</th>
            <th>GL</th>
            <th className="num">Current cost</th>
            <th className="num">Active seat tier / rate</th>
            <th className="num">Active usage tier / rate</th>
            <th className="num">Seat cost</th>
            <th className="num">Usage cost</th>
            <th className="num">Fixed</th>
            <th className="num">Floor adj.</th>
            <th className="num">Run-rate cost</th>
            <th className="num">Contractual true-up</th>
            <th className="num">Billed cost</th>
            <th className="num">Delta</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c, index) => {
            const previous = [...components]
              .slice(0, index)
              .reverse()
              .find((candidate) => candidate.driverId === c.driverId);
            const seatTransition =
              !previous ||
              previous.scenarioSeatTierLabel !== c.scenarioSeatTierLabel ||
              previous.scenarioSeatRate !== c.scenarioSeatRate;
            const usageTransition =
              !previous ||
              previous.scenarioUsageTierLabel !== c.scenarioUsageTierLabel ||
              previous.scenarioUsageRate !== c.scenarioUsageRate;
            return (
            <tr key={`${c.driverId}-${c.fiscalYear}-${c.period}`}>
              <td>
                {periodLabel(c.period)} FY{c.fiscalYear}
              </td>
              <td>
                <code className="le-code">{c.glAccount}</code>
              </td>
              <td className="num">{formatMoney(c.currentCost)}</td>
              <td className="num">
                <span className={seatTransition ? "sc-tier-transition" : ""}>
                  {c.scenarioSeatTierLabel ?? "Base"} · {formatMoney(c.scenarioSeatRate)}
                </span>
              </td>
              <td className="num">
                <span className={usageTransition ? "sc-tier-transition" : ""}>
                  {c.scenarioUsageTierLabel ?? "Base"} · {formatMoney(c.scenarioUsageRate)}
                </span>
              </td>
              <td className="num">{formatMoney(c.scenarioSeatCost)}</td>
              <td className="num">{formatMoney(c.scenarioUsageCost)}</td>
              <td className="num">{formatMoney(c.scenarioFixedCost)}</td>
              <td className="num">
                {formatMoney(c.scenarioMinimumFloorAdjustment)}
              </td>
              <td className="num">{formatMoney(c.scenarioRunRateCost)}</td>
              <td className="num">
                {c.scenarioTrueUpCost === 0 ? "—" : formatMoney(c.scenarioTrueUpCost)}
              </td>
              <td className="num"><strong>{formatMoney(c.scenarioCost)}</strong></td>
              <td className="num">
                <span
                  className={
                    c.delta > 0
                      ? "bad-text"
                      : c.delta < 0
                      ? "good-text"
                      : ""
                  }
                >
                  {c.delta === 0 ? "—" : formatVariance(c.delta)}
                </span>
              </td>
            </tr>
          )})}
        </tbody>
      </table>
    </div>
  );
}

// ─── Root export ──────────────────────────────────────────────────────────────

export function LicensingAnalytics({ scenario }: { scenario: Scenario }) {
  const analytics = scenario.licensingAnalytics ?? [];
  const components = scenario.licensingMonthlyComponents ?? [];

  if (analytics.length === 0 && components.length === 0) {
    return (
      <div className="sc-empty-state" data-testid="licensing-empty">
        <Activity size={18} />
        <p>
          No licensing analytics yet. Add a Licensing Model Change driver and
          recompute to see current-model vs scenario-model economics.
        </p>
      </div>
    );
  }

  return (
    <div className="sc-le-analytics" data-testid="licensing-analytics">
      {analytics.length > 0 && (
        <>
          <div className="sc-le-section-label">Licensing analytics</div>
          {analytics.map((a) => {
            const driver = scenario.drivers.find((item) => item.driverId === a.driverId);
            const usageUnit = driver?.licenseSnapshot?.usageUnit;
            return (
              <div key={a.driverId}>
                <AnalyticsCard analytics={a} usageUnit={usageUnit} />
                {driver?.licenseSnapshot && (
                  <div className="sc-license-snapshot sc-license-snapshot--analytics">
                    <div className="sc-license-snapshot__head">
                      <strong>Immutable captured pricing</strong>
                      <span><Info size={10} /> Server-calculated results</span>
                    </div>
                    <div>
                      <span>
                        Seat: {summarizeTiers(driver.licenseSnapshot.seatTiers, driver.licenseSnapshot.seatRate, "seat")}
                      </span>
                      <span>
                        Usage: {summarizeTiers(driver.licenseSnapshot.usageTiers, driver.licenseSnapshot.unitRate, usageUnit || "unit")}
                      </span>
                      <span>True-up: {summarizeTrueUp(driver.licenseSnapshot)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {components.length > 0 && (
        <>
          <div className="sc-le-section-label">
            Monthly contractual economics · server results
          </div>
          <ComponentsTable components={components} />
        </>
      )}
    </div>
  );
}
